import { useTranslation } from "react-i18next";

/**
 * Delivered-versus-contracted for a procurement contract.
 *
 * The bar is capped at 100% because a bar wider than its track is meaningless;
 * the number beside it is the real figure, so an over-delivered contract reads
 * "105%" with a full bar rather than silently looking exactly finished.
 *
 * A fulfilment that is not known (null) is said in words. An empty bar with
 * "0 t" would claim that nothing was delivered.
 */
export function FulfilmentBar({
  settledT,
  inProgressT,
  remainingT,
  fulfilmentPct,
}: {
  settledT: number | null;
  inProgressT: number | null;
  remainingT: number | null;
  fulfilmentPct: number | null;
}) {
  const { t } = useTranslation();
  if (fulfilmentPct == null || settledT == null || remainingT == null) {
    return (
      <span
        style={{ fontSize: 12, color: "var(--ink-3)" }}
        title={t(
          "procurement.contract.notLinkedHint",
          "The contract comes from the register. Its receipts were recorded in the ERP and are not linked to it here, so its fulfilment is not known.",
        )}
      >
        {t("procurement.contract.notLinked", "not linked to receipts")}
      </span>
    );
  }
  const width = Math.min(100, Math.max(0, fulfilmentPct));
  const over = fulfilmentPct > 100;
  return (
    <div style={{ minWidth: 140 }}>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: "var(--surface-2)",
          overflow: "hidden",
        }}
        role="img"
        aria-label={t("procurement.contract.fulfilmentAria", "{{pct}}% delivered", {
          pct: fulfilmentPct.toFixed(2),
        })}
      >
        <div
          style={{
            width: `${width}%`,
            height: "100%",
            background: over ? "var(--warn)" : "var(--ok)",
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: 11, color: "var(--ink-3)" }}>
        <span>{fulfilmentPct.toFixed(2)}%</span>
        <span title={t("procurement.contract.settledT", "Settled (t)")}>{settledT} t</span>
        {inProgressT != null && inProgressT > 0 && (
          <span title={t("procurement.contract.inProgressT", "In progress (t)")}>
            +{inProgressT} t
          </span>
        )}
        <span title={t("procurement.contract.remainingT", "Remaining (t)")}>
          {remainingT} t {t("procurement.contract.remainingShort", "left")}
        </span>
      </div>
    </div>
  );
}
